/**
 * Employee HR chat — one thread with admins. Admin names are never shown.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  chatDateKey,
  chatDayKind,
  chatTimeLabel,
  formatChatDate,
} from '../../../utils/chatMessageTime';
import { getEmployeeEmail } from '../auth/employeeAuthStorage';
import './EmployeeMessages.css';

type ChatMessage = {
  id: number;
  senderType: 'EMPLOYEE' | 'ADMIN';
  body: string;
  occurredAt: string;
};

export interface EmployeeMessagesProps {
  onBack: () => void;
}

const EmployeeMessages: React.FC<EmployeeMessagesProps> = ({ onBack }) => {
  const { t, language } = useLanguage();

  const dayLabel = (occurredAt: string) => {
    const kind = chatDayKind(occurredAt);
    if (kind === 'today') return t('empChat.today');
    if (kind === 'yesterday') return t('empChat.yesterday');
    return formatChatDate(occurredAt, language);
  };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const email = getEmployeeEmail();

  const loadThread = useCallback(async (opts?: { markRead?: boolean; silent?: boolean }) => {
    if (!email) {
      setError(t('empChat.loadError'));
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/employee/chat/thread?email=${encodeURIComponent(email)}`
      );
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      if (opts?.markRead !== false) {
        await fetch(
          `${API_BASE_URL}/api/employee/chat/thread/read?email=${encodeURIComponent(email)}`,
          { method: 'POST' }
        );
      }
      setError(null);
    } catch {
      if (!opts?.silent) setError(t('empChat.loadError'));
    } finally {
      setLoading(false);
    }
  }, [email, t]);

  useEffect(() => {
    void loadThread({ markRead: true });
  }, [loadThread]);

  useEffect(() => {
    if (!email) return;
    const id = window.setInterval(() => {
      void loadThread({ markRead: false, silent: true });
    }, 8000);
    return () => window.clearInterval(id);
  }, [email, loadThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!email || !text || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/employee/chat/thread/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, body: text }),
      });
      if (!res.ok) throw new Error('send failed');
      const msg: ChatMessage = await res.json();
      setMessages((prev) => [...prev, msg]);
      setDraft('');
    } catch {
      setError(t('empChat.sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="emp-messages">
      <div className="emp-messages__card">
        <div className="emp-messages__top">
          <button type="button" className="emp-messages__back" onClick={onBack}>
            {t('empChat.back')}
          </button>
        </div>
        <h1 className="emp-messages__title">{t('empChat.title')}</h1>
        {error && (
          <p className="emp-messages__error" role="alert">
            {error}
          </p>
        )}
        <div className="emp-messages__thread">
          {loading ? (
            <p className="emp-messages__hint">{t('empChat.loading')}</p>
          ) : messages.length === 0 ? (
            <p className="emp-messages__hint">{t('empChat.empty')}</p>
          ) : (
            messages.map((m, i) => {
              const isEmp = m.senderType === 'EMPLOYEE';
              const prevKey = i > 0 ? chatDateKey(messages[i - 1].occurredAt) : '';
              const showDay = chatDateKey(m.occurredAt) !== prevKey;
              const time = chatTimeLabel(m.occurredAt);
              return (
                <React.Fragment key={m.id}>
                  {showDay && (
                    <div className="emp-messages__day" role="separator">
                      <span>{dayLabel(m.occurredAt)}</span>
                    </div>
                  )}
                  <div
                    className={`emp-messages__row${isEmp ? ' emp-messages__row--emp' : ''}`}
                  >
                    {isEmp && time && (
                      <span className="emp-messages__time">{time}</span>
                    )}
                    <div
                      className={`emp-messages__bubble${
                        isEmp ? ' emp-messages__bubble--emp' : ' emp-messages__bubble--admin'
                      }`}
                    >
                      {m.body}
                    </div>
                    {!isEmp && time && (
                      <span className="emp-messages__time">{time}</span>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={endRef} />
        </div>
        <div className="emp-messages__composer">
          <textarea
            className="emp-messages__input"
            rows={2}
            value={draft}
            placeholder={t('empChat.placeholder')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            disabled={sending || !email}
          />
          <button
            type="button"
            className="emp-messages__send"
            onClick={() => void handleSend()}
            disabled={sending || !draft.trim() || !email}
          >
            {t('empChat.send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeMessages;
